import React, { memo, useMemo } from 'react';
import { WidgetGridProvider } from '../../../../../../../components/WidgetGrid/context';
import WidgetCardShell from '../../../../../../../components/WidgetGrid/components/WidgetCardShell/WidgetCardShell';
import ActivityListWidget from '../../../../../../../components/WidgetGrid/customWidgets/ucpWidgets/ActivityListWidget';
import NotesListWidget from '../../../../../../../components/WidgetGrid/customWidgets/ucpWidgets/NotesListWidget';
import Widget from '../../../../../../../components/WidgetGrid/components/Widget/Widget';
import {
  WIDGET_CLASS_CONSTRAINTS,
  GRID_UNIT_PX,
  GRID_COLS,
  GRID_MARGIN,
} from '../../../../../../../components/WidgetGrid/constants/widgetGridConstants';
import { COMPRESSED_LAYOUT_W_THRESHOLD } from '../../../../../../../components/WidgetGrid/customWidgets/ucpWidgets/ActivityListWidget/constants';
import WIDGET_PREVIEW_DATA from '../../../../constants/widgetPreviewData';
import {
  FullWrapper,
  PreviewCardContainer,
  ThumbnailScale,
  ThumbnailWrapper,
} from './WidgetPreviewRenderer.styled';

const PREVIEW_REGISTRY = Object.freeze({
  activity: ActivityListWidget,
  notes: NotesListWidget,
});

const ACTION_BUTTON_MAP = Object.freeze({
  refresh: { key: 'BTN_REFRESH', icon: 'autorenew', tooltip: 'Update' },
  expand: { key: 'BTN_EXPAND', icon: 'openInNew', tooltip: 'Expand' },
  add: { key: 'BTN_ADD', icon: 'plus', tooltip: 'Add' },
});

const MAX_H_CAP = 14;

const buildHeaderButtons = (defaultActions) => {
  if (!defaultActions) return [];
  return defaultActions
    .map((action) => ACTION_BUTTON_MAP[action])
    .filter(Boolean);
};

const getPreviewDimensions = (widgetClass, size) => {
  const constraints = WIDGET_CLASS_CONSTRAINTS[widgetClass];
  if (!constraints) return null;

  const cappedMaxH = Math.min(constraints.maxH, MAX_H_CAP);
  let w;
  let h;

  switch (size) {
    case 'small':
      w = constraints.minW;
      h = constraints.minH;
      break;
    case 'medium':
      w = Math.round((constraints.minW + constraints.maxW) / 2);
      h = Math.round((constraints.minH + cappedMaxH) / 2);
      break;
    case 'large':
    default:
      w = constraints.maxW;
      h = cappedMaxH;
      break;
  }

  return {
    widthPercent: Math.round((w / GRID_COLS) * 100),
    heightPx: h * GRID_UNIT_PX + (h - 1) * GRID_MARGIN[1],
    isCompressed: w <= COMPRESSED_LAYOUT_W_THRESHOLD,
  };
};

const WidgetPreviewRenderer = ({ widget, mode = 'thumbnail', selectedSize = 'large' }) => {
  const previewData = WIDGET_PREVIEW_DATA[widget.type] ?? null;

  const headerButtons = useMemo(
    () => buildHeaderButtons(widget.defaultActions),
    [widget.defaultActions],
  );

  const dimensions = useMemo(
    () => (mode === 'full' ? getPreviewDimensions(widget.widgetClass, selectedSize) : null),
    [widget.widgetClass, selectedSize, mode],
  );

  const isCompressed = mode === 'thumbnail' || (dimensions?.isCompressed ?? false);

  const content = useMemo(() => {
    if (!previewData) return null;

    const WidgetComponent = PREVIEW_REGISTRY[widget.type];

    if (WidgetComponent) {
      return <WidgetComponent data={previewData} isCompressed={isCompressed} />;
    }

    return <Widget data={previewData} />;
  }, [widget.type, previewData, isCompressed]);

  if (!content) return null;

  const widgetCard = (
    <WidgetGridProvider isEditMode={false}>
      <PreviewCardContainer
        $widthPercent={dimensions?.widthPercent}
        $heightPx={dimensions?.heightPx}
      >
        <WidgetCardShell title={widget.name} headerButtons={headerButtons}>
          {content}
        </WidgetCardShell>
      </PreviewCardContainer>
    </WidgetGridProvider>
  );

  if (mode === 'full') {
    return <FullWrapper>{widgetCard}</FullWrapper>;
  }

  return (
    <ThumbnailWrapper>
      <ThumbnailScale>{widgetCard}</ThumbnailScale>
    </ThumbnailWrapper>
  );
};

export default memo(WidgetPreviewRenderer);
